// components/Header.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { LogOut, ChevronDown, Menu, X, User, Compass } from 'lucide-react';

// Icon mapping for menu items
const iconMap = {
  'ri:dashboard-line': Icons.LayoutDashboard,
  'dashboard': Icons.LayoutDashboard,
  'client': Icons.Users,
  'Client': Icons.Users,
  'user': Icons.User,
  'settings': Icons.Settings,
  'report': Icons.BarChart,
  'booking': Icons.Calendar,
  'activity': Icons.Activity,
  'country': Icons.Globe,
  'destination': Icons.MapPin,
  'category': Icons.LayoutGrid,
  'commission': Icons.DollarSign,
  'profile': Icons.UserCircle,
  'stats': Icons.BarChart,
  'bulkupload': Icons.Upload,
  'assignment': Icons.UserPlus,
  'default': Icons.Circle
};

// Mobile Menu Item Component
const MobileMenuItem = ({ item, handleNavigation, isActive, level = 0 }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  
  if (hasChildren) {
    return (
      <div key={item.id || item.table_id}>
        <button
          onClick={() => setOpen(!open)}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all ${
            isActive(item.url) 
              ? 'bg-amber-100 text-amber-700' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <div className="flex items-center gap-2">
            {getMobileIcon(item.icon || item.id)}
            <span>{item.title}</span>
          </div>
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="ml-4 mt-1 space-y-1">
            {item.children.map(child => (
              <MobileMenuItem 
                key={child.id || child.table_id}
                item={child}
                handleNavigation={handleNavigation}
                isActive={isActive}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <button
      key={item.id || item.table_id}
      onClick={() => handleNavigation(item.url)}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
        isActive(item.url) 
          ? 'bg-amber-100 text-amber-700' 
          : 'text-gray-600 hover:bg-gray-100'
      }`}
      style={{ paddingLeft: level > 0 ? `${24 + level * 8}px` : '12px' }}
    >
      {getMobileIcon(item.icon || item.id)}
      <span>{item.title}</span>
    </button>
  );
};

// Desktop Menu Item Component
const DesktopMenuItem = ({ item, openDropdown, setOpenDropdown, handleNavigation, isActive, level = 0 }) => {
  const hasChildren = item.children && item.children.length > 0;
  const active = isActive(item.url);
  
  if (hasChildren) {
    return (
      <div key={item.id || item.table_id} className="relative">
        <button
          onClick={() => setOpenDropdown(openDropdown === item.id ? null : item.id)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            active 
              ? 'bg-amber-100 text-amber-700' 
              : 'text-gray-600 hover:bg-gray-100 hover:text-amber-600'
          }`}
        >
          {getIcon(item.icon || item.id)}
          <span>{item.title}</span>
          <ChevronDown size={14} className={`transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
        </button>
        
        {openDropdown === item.id && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[200px] z-50">
            {item.children.map(child => (
              <DesktopMenuItem
                key={child.id || child.table_id}
                item={child}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                handleNavigation={handleNavigation}
                isActive={isActive}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <button
      key={item.id || item.table_id}
      onClick={() => handleNavigation(item.url)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
        active 
          ? 'bg-amber-100 text-amber-700' 
          : 'text-gray-600 hover:bg-gray-100 hover:text-amber-600'
      }`}
      style={{ paddingLeft: level > 0 ? `${24 + level * 8}px` : undefined }}
    >
      {getIcon(item.icon || item.id)}
      <span>{item.title}</span>
    </button>
  );
};

// Helper functions for icons
const getIcon = (iconName) => {
  if (!iconName) {
    return <Icons.Circle size={18} className="text-amber-500" />;
  }
  const IconComponent = iconMap[iconName.toLowerCase()] || iconMap[iconName] || Icons.Circle;
  return <IconComponent size={18} className="text-amber-500" />;
};

const getMobileIcon = (iconName) => {
  if (!iconName) {
    return <Icons.Circle size={16} className="text-amber-500" />;
  }
  const IconComponent = iconMap[iconName.toLowerCase()] || iconMap[iconName] || Icons.Circle;
  return <IconComponent size={16} className="text-amber-500" />;
};

const Header = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Load menu items from localStorage
  useEffect(() => {
    const rawNavList = localStorage.getItem('nav_list');
    
    if (rawNavList) {
      try {
        const parsedNav = JSON.parse(rawNavList);
        setMenuItems(parsedNav);
      } catch (err) {
        console.error("Error parsing nav_list:", err);
        setDefaultMenuItems();
      }
    } else {
      setDefaultMenuItems();
    }
  }, []);

  // Default menu items if nav_list is not available
  const setDefaultMenuItems = () => {
    const defaultItems = [
      {
        id: 'dashboard',
        title: 'Dashboard',
        url: '/dashboard',
        icon: 'dashboard'
      },
      {
        id: 'stats',
        title: 'Statistics',
        url: '/stats',
        icon: 'stats'
      }
    ];
    
    // Add admin specific menus
    if (user?.role_code === 'ADMIN') {
      defaultItems.push(
        {
          id: 'client-management',
          title: 'Client Management',
          url: '/client-management',
          icon: 'client'
        },
        {
          id: 'assignment',
          title: 'Assignment',
          url: '/assignment',
          icon: 'assignment'
        },
        {
          id: 'bulkupload',
          title: 'Bulk Upload',
          url: '/bulkupload',
          icon: 'bulkupload'
        }
      );
    }
    
    setMenuItems(defaultItems);
  };

  const handleNavigation = (url) => {
    if (url) {
      navigate(url);
      setMobileMenuOpen(false);
      setOpenDropdown(null);
    }
  };

  const isActive = (url) => {
    return location.pathname === url;
  };

  const handleLogoutClick = () => {
    if (onLogout) {
      onLogout();
    }
    navigate('/login');
  };

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <div 
                onClick={() => handleNavigation('/dashboard')}
                className="flex items-center gap-2 cursor-pointer group"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <Compass className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    TravSky
                  </h1>
                  <p className="text-xs text-gray-500 hidden sm:block">Tour & Travel</p>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {menuItems.map(item => (
                <DesktopMenuItem
                  key={item.id || item.table_id}
                  item={item}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  handleNavigation={handleNavigation}
                  isActive={isActive}
                />
              ))}
            </nav>

            {/* User Menu */}
            {user && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                    <User size={16} className="text-amber-600" />
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm font-semibold text-gray-700">{user.user_name}</p>
                    <p className="text-xs text-gray-500">{user.role_name}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogoutClick}
                  className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"
                  title="Logout"
                >
                  <LogOut size={18} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="px-4 py-3 space-y-1">
              {menuItems.map(item => (
                <MobileMenuItem
                  key={item.id || item.table_id}
                  item={item}
                  handleNavigation={handleNavigation}
                  isActive={isActive}
                />
              ))}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;